import { useState, useEffect, useCallback, useMemo } from "react";
import {
    Plus, Loader2, Trash2, Check, AlertTriangle, Search, X, Lightbulb,
} from "lucide-react";
import { Button } from "../ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { CUSTOMER_PAIN_POINTS_URL } from "../../constants/api";
import ToastMsg from "../common/ToastMsg";
import { cn } from "../../utils/tailwindUtils";
import { formatDateTime } from "../../utils/datetime";

const SEVERITY_STYLE = {
    high:   "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-900/50",
    medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-900/50",
    low:    "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300 border-slate-200 dark:border-slate-600",
};

const SEVERITY_ORDER = ["high", "medium", "low"];

export default function PainPointsPanel({ customerId }) {
    const { fetch, create, update, remove } = useApiMethods();

    const [rows, setRows] = useState([]);
    const [available, setAvailable] = useState([]);
    const [severities, setSeverities] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState("");

    // add form
    const [adding, setAdding] = useState(false);
    const [picked, setPicked] = useState([]);
    const [severity, setSeverity] = useState("medium");
    const [notes, setNotes] = useState("");
    const [search, setSearch] = useState("");

    // inline edit
    const [editId, setEditId] = useState(null);
    const [editNotes, setEditNotes] = useState("");

    const load = useCallback(() => {
        if (!customerId) return;
        setLoading(true);
        fetch(`${CUSTOMER_PAIN_POINTS_URL}?customer_id=${customerId}`)
            .then(res => {
                if (res?.status === "success") {
                    setRows(res.data || []);
                    setAvailable(res.available || []);
                    setSeverities(res.severities || []);
                    setSummary(res.summary || null);
                } else {
                    ToastMsg("error", res?.message || "Could not load pain points");
                }
            })
            .catch(() => ToastMsg("error", "Could not load pain points"))
            .finally(() => setLoading(false));
    }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const grouped = useMemo(() => {
        const q = search.trim().toLowerCase();
        const list = q
            ? available.filter(a =>
                a.name.toLowerCase().includes(q)
                || (a.category || "").toLowerCase().includes(q)
                || (a.description || "").toLowerCase().includes(q))
            : available;
        const out = [];
        list.forEach(a => {
            const key = a.category || "Other";
            let g = out.find(x => x.category === key);
            if (!g) { g = { category: key, items: [] }; out.push(g); }
            g.items.push(a);
        });
        return out;
    }, [available, search]);

    const toggle = (id) =>
        setPicked(p => (p.includes(id) ? p.filter(x => x !== id) : [...p, id]));

    const save = () => {
        if (!picked.length) return ToastMsg("error", "Pick at least one pain point");
        setBusy("add");
        create(CUSTOMER_PAIN_POINTS_URL, {
            customer_id: customerId, pain_point_ids: picked,
            severity, notes: notes.trim(),
        })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", res.message);
                    setPicked([]); setNotes(""); setSeverity("medium");
                    setSearch(""); setAdding(false);
                    load();
                } else ToastMsg("error", res?.message || "Could not save");
            })
            .catch(err => ToastMsg("error", err?.response?.data?.message || "Could not save"))
            .finally(() => setBusy(""));
    };

    const patch = (row, body, okMsg) => {
        setBusy(`row-${row.id}`);
        update(CUSTOMER_PAIN_POINTS_URL, { id: row.id, ...body })
            .then(res => {
                if (res?.status === "success") {
                    if (okMsg) ToastMsg("success", okMsg);
                    setEditId(null);
                    load();
                } else ToastMsg("error", res?.message || "Could not save");
            })
            .catch(() => ToastMsg("error", "Could not save"))
            .finally(() => setBusy(""));
    };

    const drop = (row) => {
        setBusy(`row-${row.id}`);
        remove(CUSTOMER_PAIN_POINTS_URL, { id: row.id })
            .then(res => {
                if (res?.status === "success") { ToastMsg("success", "Removed"); load(); }
                else ToastMsg("error", res?.message || "Could not remove");
            })
            .catch(() => ToastMsg("error", "Could not remove"))
            .finally(() => setBusy(""));
    };

    if (loading && !rows.length && !available.length) {
        return (
            <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
            </div>
        );
    }

    // Nothing in the master list at all — say how to fix it rather than showing
    // an empty box that looks broken.
    if (!summary?.catalog_size) {
        return (
            <div className="text-center py-14 px-6">
                <Lightbulb className="h-8 w-8 mx-auto text-gray-300 dark:text-gray-600" />
                <p className="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                    No pain points defined yet
                </p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                    The list of problems your team asks about is empty, so there is nothing
                    to pick from. An admin can add them and they will appear here for every lead.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* ── summary + add button ── */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-4 text-xs">
                    <span className="text-gray-600 dark:text-gray-300">
                        <strong className="text-gray-900 dark:text-white">{summary.total}</strong> confirmed
                    </span>
                    {summary.high > 0 && (
                        <span className="text-red-600 dark:text-red-400 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {summary.high} deal breaker{summary.high > 1 ? "s" : ""}
                        </span>
                    )}
                    {summary.total > 0 && (
                        <span className="text-gray-500 dark:text-gray-400">
                            {summary.unresolved} not yet answered
                        </span>
                    )}
                </div>
                <Button size="sm" variant={adding ? "outline" : "default"}
                    onClick={() => { setAdding(a => !a); setPicked([]); setSearch(""); }}
                    className="text-xs">
                    {adding ? <><X className="h-3.5 w-3.5 mr-1" />Cancel</>
                            : <><Plus className="h-3.5 w-3.5 mr-1" />Record pain points</>}
                </Button>
            </div>

            {/* ── add form: chip list, one click per pain ── */}
            {adding && (
                <div className="rounded-xl border border-blue-200 dark:border-blue-900/50 bg-blue-50/50 dark:bg-blue-900/10 p-4 space-y-3">
                    {available.length === 0 ? (
                        <p className="text-xs text-gray-600 dark:text-gray-300">
                            Every pain point on the list is already recorded for this lead.
                        </p>
                    ) : (
                        <>
                            <div className="relative">
                                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                <input value={search} onChange={e => setSearch(e.target.value)}
                                    placeholder="Filter the list…"
                                    className="w-full pl-8 pr-3 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400" />
                            </div>

                            <div className="max-h-64 overflow-y-auto space-y-3 pr-1">
                                {grouped.map(g => (
                                    <div key={g.category}>
                                        <p className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium mb-1.5">
                                            {g.category}
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {g.items.map(a => (
                                                <button key={a.id} onClick={() => toggle(a.id)}
                                                    title={a.description || undefined}
                                                    className={cn(
                                                        "px-2.5 py-1 rounded-full text-[11px] border transition-colors text-left",
                                                        picked.includes(a.id)
                                                            ? "bg-blue-600 text-white border-blue-700"
                                                            : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-blue-400")}>
                                                    {picked.includes(a.id) && <Check className="h-2.5 w-2.5 inline mr-1" />}
                                                    {a.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                                {!grouped.length && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Nothing matches "{search}".
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-wrap items-center gap-2 pt-1">
                                <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400 font-medium">
                                    How bad
                                </span>
                                {severities.map(s => (
                                    <button key={s.value} onClick={() => setSeverity(s.value)}
                                        className={cn("px-2.5 py-1 rounded-full text-[11px] border transition-colors",
                                            severity === s.value
                                                ? SEVERITY_STYLE[s.value]
                                                : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700")}>
                                        {s.label}
                                    </button>
                                ))}
                            </div>

                            <textarea value={notes} onChange={e => setNotes(e.target.value)}
                                rows={2} placeholder="What did they actually say? (optional, but this is what wins the deal review)"
                                className="w-full px-3 py-2 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400 resize-none" />

                            <div className="flex items-center gap-2">
                                <Button size="sm" onClick={save}
                                    disabled={busy === "add" || !picked.length} className="text-xs">
                                    {busy === "add" && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                                    Save {picked.length ? `${picked.length} pain point${picked.length > 1 ? "s" : ""}` : ""}
                                </Button>
                                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                    The severity and note apply to everything selected.
                                </span>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* ── confirmed list ── */}
            {rows.length === 0 && !adding && (
                <div className="text-center py-10 px-6 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                    <Lightbulb className="h-7 w-7 mx-auto text-gray-300 dark:text-gray-600" />
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                        No pain points recorded for this lead
                    </p>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {summary.catalog_size} to choose from. Recording even one makes the
                        next conversation easier.
                    </p>
                </div>
            )}

            <div className="space-y-2">
                {SEVERITY_ORDER.flatMap(level => rows.filter(r => r.severity === level)).map(row => (
                    <div key={row.id}
                        className={cn("rounded-xl border p-3",
                            row.is_resolved
                                ? "border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/30"
                                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/60")}>
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2">
                                    <span className={cn("text-sm font-medium",
                                        row.is_resolved
                                            ? "text-gray-500 dark:text-gray-400 line-through"
                                            : "text-gray-900 dark:text-white")}>
                                        {row.name}
                                    </span>
                                    <span className={cn("px-2 py-0.5 rounded-full text-[10px] font-medium border",
                                        SEVERITY_STYLE[row.severity])}>
                                        {row.severity_label}
                                    </span>
                                    {row.category && (
                                        <span className="text-[10px] text-gray-400 dark:text-gray-500">
                                            {row.category}
                                        </span>
                                    )}
                                </div>

                                {row.answered_by && (
                                    <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-400">
                                        We answer this with: {row.answered_by}
                                    </p>
                                )}

                                {editId === row.id ? (
                                    <div className="mt-2 space-y-2">
                                        <textarea value={editNotes} rows={2} autoFocus
                                            onChange={e => setEditNotes(e.target.value)}
                                            className="w-full px-2.5 py-1.5 text-xs rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 outline-none focus:ring-1 focus:ring-blue-400 resize-none" />
                                        <div className="flex items-center gap-2">
                                            <Button size="sm" className="text-[11px] h-6 px-2"
                                                onClick={() => patch(row, { notes: editNotes }, "Note saved")}
                                                disabled={busy === `row-${row.id}`}>Save</Button>
                                            <button onClick={() => setEditId(null)}
                                                className="text-[11px] text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <p onClick={() => { setEditId(row.id); setEditNotes(row.notes || ""); }}
                                        className={cn("mt-1.5 text-xs cursor-text",
                                            row.notes
                                                ? "text-gray-600 dark:text-gray-300 whitespace-pre-wrap"
                                                : "text-gray-400 dark:text-gray-500 italic")}>
                                        {row.notes || "Add what they said…"}
                                    </p>
                                )}

                                <p className="mt-1.5 text-[10px] text-gray-400 dark:text-gray-500">
                                    Recorded {formatDateTime(row.captured_at)}
                                </p>
                            </div>

                            <div className="flex items-center gap-1 shrink-0">
                                <select value={row.severity}
                                    onChange={e => patch(row, { severity: e.target.value })}
                                    disabled={busy === `row-${row.id}`}
                                    className="text-[10px] rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1 py-0.5">
                                    {severities.map(s => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </select>
                                <button
                                    title={row.is_resolved ? "Mark as still open" : "We have shown them how we fix it"}
                                    onClick={() => patch(row, { is_resolved: !row.is_resolved })}
                                    disabled={busy === `row-${row.id}`}
                                    className={cn("p-1 rounded transition-colors",
                                        row.is_resolved
                                            ? "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20"
                                            : "text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20")}>
                                    <Check className="h-3.5 w-3.5" />
                                </button>
                                <button title="Remove" onClick={() => drop(row)}
                                    disabled={busy === `row-${row.id}`}
                                    className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
